import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
  createAdminSource,
  deleteAdminSource,
  getAdminProfiles,
  getAdminSources,
  updateAdminSource,
} from "@/api/admin";
import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import AdminLayout from "@/components/admin/AdminLayout";
import type { AdminSite, AdminSiteCreate, AdminSiteUpdate } from "@/types";

type Priority = AdminSite["priority"];

const EMPTY_FORM: AdminSiteCreate = {
  profile_slug: "",
  name: "",
  url: "",
  priority: "media",
};

const URL_PREFIX_RE = /^https?:\/\//i;

function sourceSaveErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return "No se pudo guardar los cambios.";
  }
  if (err.status === 401) {
    return "Sesión expirada. Vuelve a iniciar sesión en el panel admin.";
  }
  if (err.status === 409) {
    return "Ya existe otra fuente con esa URL.";
  }
  if (err.status === 400 && err.message.includes("http")) {
    return "La URL debe empezar por http:// o https://";
  }
  if (err.status >= 500 || err.message === "Request failed") {
    return "No se pudo guardar la fuente. Inténtalo de nuevo o revisa si la URL ya existe.";
  }
  return err.message;
}

function priorityBadge(priority: Priority) {
  const styles: Record<Priority, string> = {
    alta: "bg-red-100 text-red-800",
    media: "bg-amber-100 text-amber-800",
    baja: "bg-slate-100 text-slate-700",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[priority]}`}>
      {priority}
    </span>
  );
}

export default function AdminSources() {
  const queryClient = useQueryClient();
  const [profileSlug, setProfileSlug] = useState("");
  const [form, setForm] = useState<AdminSiteCreate>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<AdminSiteUpdate>({});
  const [deleteTarget, setDeleteTarget] = useState<AdminSite | null>(null);
  const [hardDelete, setHardDelete] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const profiles = useQuery({
    queryKey: queryKeys.admin.profiles(),
    queryFn: getAdminProfiles,
  });

  const activeSlug = profileSlug || profiles.data?.[0]?.slug || "";

  const sources = useQuery({
    queryKey: queryKeys.admin.sources(activeSlug),
    queryFn: () => getAdminSources(activeSlug, true),
    enabled: Boolean(activeSlug),
  });

  const invalidateSources = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.admin.sources(activeSlug) });
  };

  const createMutation = useMutation({
    mutationFn: createAdminSource,
    onSuccess: async () => {
      setForm({ ...EMPTY_FORM, profile_slug: activeSlug });
      setFormError(null);
      await invalidateSources();
    },
    onError: (err: unknown) => {
      setFormError(sourceSaveErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: AdminSiteUpdate }) =>
      updateAdminSource(id, body),
    onSuccess: async () => {
      setEditingId(null);
      setEditForm({});
      setEditError(null);
      await invalidateSources();
    },
    onError: (err: unknown) => {
      setEditError(sourceSaveErrorMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, mode }: { id: number; mode: "soft" | "hard" }) =>
      deleteAdminSource(id, mode),
    onSuccess: async () => {
      setDeleteTarget(null);
      setHardDelete(false);
      await invalidateSources();
    },
  });

  const sortedSources = useMemo(
    () => (sources.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [sources.data],
  );

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSlug) return;
    createMutation.mutate({ ...form, profile_slug: activeSlug });
  };

  const startEdit = (site: AdminSite) => {
    setEditingId(site.id);
    setEditError(null);
    setEditForm({
      name: site.name,
      url: site.url,
      priority: site.priority,
      is_active: site.is_active,
    });
  };

  const handleSaveEdit = () => {
    if (editingId === null || updateMutation.isPending) return;

    const name = (editForm.name ?? "").trim();
    const url = (editForm.url ?? "").trim();

    if (!name) {
      setEditError("El nombre es obligatorio.");
      return;
    }
    if (!url) {
      setEditError("La URL es obligatoria.");
      return;
    }
    if (!URL_PREFIX_RE.test(url)) {
      setEditError("La URL debe empezar por http:// o https://");
      return;
    }

    setEditError(null);
    updateMutation.mutate({
      id: editingId,
      body: { ...editForm, name, url },
    });
  };

  return (
    <AdminLayout title="Fuentes">
      <div className="space-y-8">
        <section className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="profile-select" className="block text-sm font-medium text-slate-700">
              Perfil
            </label>
            <select
              id="profile-select"
              value={activeSlug}
              onChange={(e) => setProfileSlug(e.target.value)}
              className="mt-1 min-w-[12rem] rounded-lg border border-slate-300 px-3 py-2"
            >
              {(profiles.data ?? []).map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.name} ({p.slug})
                </option>
              ))}
            </select>
          </div>
          <p className="text-sm text-slate-600">
            {profiles.isError
              ? "Error al cargar perfiles (¿base de datos accesible?)"
              : sources.isLoading
                ? "Cargando…"
                : `${sortedSources.length} fuente(s)`}
          </p>
        </section>

        {profiles.isError ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            No se pudieron cargar los perfiles. Comprueba la conexión a la base de datos en el servidor.
          </p>
        ) : null}

        {profiles.isSuccess && profiles.data.length === 0 ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="status">
            No hay perfiles en la base de datos. Ejecuta el seed en prod:{" "}
            <code className="font-mono">make tf-seed-iberdrola</code>
          </p>
        ) : null}

        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium">Añadir fuente</h2>
          <form className="mt-4 grid gap-4 md:grid-cols-2" onSubmit={handleCreate}>
            <div>
              <label htmlFor="new-name" className="block text-sm font-medium text-slate-700">
                Nombre
              </label>
              <input
                id="new-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                required
              />
            </div>
            <div>
              <label htmlFor="new-url" className="block text-sm font-medium text-slate-700">
                URL
              </label>
              <input
                id="new-url"
                type="url"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://…"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                required
              />
            </div>
            <div>
              <label htmlFor="new-priority" className="block text-sm font-medium text-slate-700">
                Prioridad
              </label>
              <select
                id="new-priority"
                value={form.priority}
                onChange={(e) =>
                  setForm((f) => ({ ...f, priority: e.target.value as Priority }))
                }
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              >
                <option value="alta">alta</option>
                <option value="media">media</option>
                <option value="baja">baja</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={createMutation.isPending || !form.name || !form.url}
                className="rounded-lg bg-emerald-700 px-4 py-2 font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
              >
                {createMutation.isPending ? "Guardando…" : "Añadir"}
              </button>
            </div>
          </form>
          {formError ? (
            <p className="mt-3 text-sm text-red-600" role="alert">
              {formError}
            </p>
          ) : null}
        </section>

        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-700">Nombre</th>
                <th className="px-4 py-3 text-left font-medium text-slate-700">URL</th>
                <th className="px-4 py-3 text-left font-medium text-slate-700">Prioridad</th>
                <th className="px-4 py-3 text-left font-medium text-slate-700">Estado</th>
                <th className="px-4 py-3 text-right font-medium text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedSources.map((site) => (
                <tr key={site.id} className={site.is_active ? "" : "bg-slate-50/80 opacity-70"}>
                  {editingId === site.id ? (
                    <td colSpan={5} className="px-4 py-4">
                      <div className="grid gap-3 md:grid-cols-4">
                        <input
                          value={editForm.name ?? ""}
                          onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                          className="rounded-lg border border-slate-300 px-3 py-2"
                          aria-label="Nombre de la fuente"
                        />
                        <input
                          value={editForm.url ?? ""}
                          onChange={(e) => setEditForm((f) => ({ ...f, url: e.target.value }))}
                          type="url"
                          className="rounded-lg border border-slate-300 px-3 py-2"
                          aria-label="URL de la fuente"
                        />
                        <select
                          value={editForm.priority ?? "media"}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              priority: e.target.value as Priority,
                            }))
                          }
                          className="rounded-lg border border-slate-300 px-3 py-2"
                          aria-label="Prioridad"
                        >
                          <option value="alta">alta</option>
                          <option value="media">media</option>
                          <option value="baja">baja</option>
                        </select>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={handleSaveEdit}
                            disabled={updateMutation.isPending}
                            className="rounded-lg bg-emerald-700 px-3 py-1.5 text-white disabled:opacity-50"
                          >
                            {updateMutation.isPending ? "Guardando…" : "Guardar"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(null);
                              setEditForm({});
                              setEditError(null);
                            }}
                            disabled={updateMutation.isPending}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-50"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                      {editError ? (
                        <p className="mt-2 text-sm text-red-600" role="alert">
                          {editError}
                        </p>
                      ) : null}
                    </td>
                  ) : (
                    <>
                      <td className="px-4 py-3 font-medium">{site.name}</td>
                      <td className="max-w-xs truncate px-4 py-3 text-slate-600" title={site.url}>
                        <a
                          href={site.url}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline"
                        >
                          {site.url}
                        </a>
                      </td>
                      <td className="px-4 py-3">{priorityBadge(site.priority)}</td>
                      <td className="px-4 py-3">
                        {site.is_active ? (
                          <span className="text-emerald-700">Activa</span>
                        ) : (
                          <span className="text-slate-500">Inactiva</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(site)}
                            className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              updateMutation.mutate({
                                id: site.id,
                                body: { is_active: !site.is_active },
                              })
                            }
                            className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                          >
                            {site.is_active ? "Desactivar" : "Reactivar"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(site)}
                            className="rounded border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {!sources.isLoading && sortedSources.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    No hay fuentes para este perfil.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </section>
      </div>

      {deleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <h3 className="text-lg font-medium">Eliminar fuente</h3>
            <p className="mt-2 text-sm text-slate-600">
              ¿Eliminar <strong>{deleteTarget.name}</strong>?
            </p>
            <label className="mt-4 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={hardDelete}
                onChange={(e) => setHardDelete(e.target.checked)}
              />
              Borrado forzado (elimina la fila de la base de datos)
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Sin marcar, solo se desactiva (soft delete).
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setDeleteTarget(null);
                  setHardDelete(false);
                }}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() =>
                  deleteMutation.mutate({
                    id: deleteTarget.id,
                    mode: hardDelete ? "hard" : "soft",
                  })
                }
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                {hardDelete ? "Borrar definitivamente" : "Desactivar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
}
